/**
 * AdminComingSoonPanel — Tier 0 module-local placeholder for admin
 * routes whose per-surface ticket hasn't landed yet.
 *
 * SCRUM-116 registers the `/admin/*` route skeleton in advance of the
 * surface-specific tickets (SCRUM-26 Overview, SCRUM-119+ surfaces).
 * Each placeholder page renders this panel with the ticket reference
 * so the team can see at a glance what's blocking which route.
 *
 * Disjoint from the existing `apps/admin/src/modules/identity/_components/
 * ComingSoonPanel.tsx` — Hybrid Z (ADR `frontend-0001`) forbids
 * cross-module imports into another slice's `_components/`. The
 * surface is small enough (one component, ~30 lines) that a local
 * implementation is cleaner than promoting a shared primitive. If a
 * third slice grows the same need, promote at that point per the
 * trap-promotion ladder.
 */

import * as React from 'react';

export interface AdminComingSoonPanelProps {
  /** Human label for the surface ("Companies list", "Add staff form"…). */
  readonly surface: string;
  /**
   * Jira key that ships the real surface ("SCRUM-119"…), when one is filed.
   * Omit for surfaces whose build ticket is still upstream (design phase) —
   * e.g. the SCRUM-114 account-menu destinations — so the panel doesn't cite
   * a Jira key that doesn't exist yet.
   */
  readonly ticket?: string;
}

export function AdminComingSoonPanel({
  surface,
  ticket,
}: AdminComingSoonPanelProps): React.ReactElement {
  return (
    <section
      aria-labelledby="admin-coming-soon-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="admin-coming-soon-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        {surface}
      </h1>
      <p
        className="mt-2 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        {ticket !== undefined ? (
          <>
            Coming soon — tracked under{' '}
            <span className="font-mono" style={{ color: 'var(--fg-primary)' }}>
              {ticket}
            </span>
            .
          </>
        ) : (
          'Coming soon.'
        )}
      </p>
    </section>
  );
}
